import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Button,
  CircularProgress,
  Alert,
  Divider,
  Tab,
  Tabs,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Tooltip,
  Avatar,
  LinearProgress,
  TablePagination
} from "@mui/material";
import {
  AccountBalance as AccountBalanceIcon,
  TrendingUp as TrendingUpIcon,
  CalendarToday as CalendarIcon,
  Download as DownloadIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  BarChart as BarChartIcon,
  ShowChart as ShowChartIcon,
  FilterList as FilterIcon,
  PictureAsPdf as PdfIcon,
  TableChart as TableIcon,
  Print as PrintIcon,
  NavigateBefore as NavigateBeforeIcon,
  NavigateNext as NavigateNextIcon
} from "@mui/icons-material";
import { Line, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler
} from "chart.js";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  ChartTooltip,
  Legend,
  Filler
);

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const GratuityTab = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [gratuityData, setGratuityData] = useState(null);
  const [trendData, setTrendData] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const [filterFY, setFilterFY] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [chartType, setChartType] = useState("line");
  const [employeeId, setEmployeeId] = useState("");
  const [companyId, setCompanyId] = useState("");
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [availableFY, setAvailableFY] = useState([]);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          navigate('/login');
          return;
        }
        
        const payload = JSON.parse(atob(token.split('.')[1]));
        setCompanyId(payload.company_id);
        
        const userResponse = await axios.get(`${API_URL}/api/user/current`, {
          withCredentials: true
        });
        
        if (userResponse.data && userResponse.data.employee_id) {
          setEmployeeId(userResponse.data.employee_id);
          fetchGratuityData(userResponse.data.employee_id, payload.company_id, page + 1, rowsPerPage);
        }
      } catch (err) {
        //console.error("Error fetching user data:", err);
        alert("session expired! Login again");
        navigate('/');
        setError("Failed to load user data");
        setLoading(false);
      }
    };
    
    fetchUserData();
  }, [navigate]);

  // Fetch data when page or rowsPerPage changes
  useEffect(() => {
    if (employeeId && companyId) {
      fetchGratuityData(employeeId, companyId, page + 1, rowsPerPage);
    }
  }, [page, rowsPerPage, employeeId, companyId]);

  const fetchGratuityData = async (empId, compId, pageNum = 1, limitNum = 10) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const summaryRes = await axios.get(
        `${API_URL}/api/gratuity/summary/${empId}?page=${pageNum}&limit=${limitNum}`,
        {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      }
      );
      
      if (summaryRes.data.success) {
        setGratuityData(summaryRes.data.data);
        setAvailableFY(summaryRes.data.data.available_financial_years || []);
      }
      
      const trendRes = await axios.get(
        `${API_URL}/api/gratuity/trend/${empId}`,
        {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        withCredentials: true
      }
      );
      
      if (trendRes.data.success) {
        setTrendData(trendRes.data.data);
      }
      
      setError(null);
    } catch (err) {
      //console.error("Error fetching gratuity data:", err);
      setError(err.response?.data?.message || "Failed to load gratuity data");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '0';
    return new Intl.NumberFormat('en-IN', {
      maximumFractionDigits: 0,
      minimumFractionDigits: 0
    }).format(value);
  };

  const formatCurrencyWithSymbol = (value) => {
    if (value === null || value === undefined) return 'N/A';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  const capitalizeWords = (str) => {
    if (!str) return '';
    return str.replace(/\b\w/g, char => char.toUpperCase());
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleViewPayslip = (payslipId) => {
    navigate(`/payslip/${payslipId}`);
  };

  const handleDownloadReport = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/gratuity/report/${employeeId}`,
        { withCredentials: true,
          headers:{
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          }
         }
      );
      
      if (response.data.success) {
        const dataStr = JSON.stringify(response.data.data, null, 2);
        const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
        const exportFileDefaultName = `gratuity_report_${employeeId}_${new Date().toISOString().split('T')[0]}.json`;
        
        const linkElement = document.createElement('a');
        linkElement.setAttribute('href', dataUri);
        linkElement.setAttribute('download', exportFileDefaultName);
        linkElement.click();
      }
    } catch (err) {
      //console.error("Error downloading report:", err);
      alert("Failed to download report");
    }
  };

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Export to PDF function
  const exportToPDF = () => {
    if (!gratuityData) return;
    
    const doc = new jsPDF('landscape');
    
    // Add title
    doc.setFontSize(18);
    doc.text('Gratuity Statement', 14, 22);
    doc.setFontSize(11);
    doc.text(`Employee: ${gratuityData.employee_name} (${gratuityData.employee_id})`, 14, 30);
    doc.text(`Generated on: ${formatDate(new Date())}`, 14, 36);
    
    // Add summary
    doc.setFontSize(10);
    doc.text(`Total Accumulated: ${gratuityData.total_gratuity_accumulated}`, 14, 45);
    doc.text(`Current FY: ${gratuityData.current_fy_gratuity}`, 14, 51);
    doc.text(`Projected Annual: ${gratuityData.projected_annual_gratuity}`, 80, 51);
    doc.text(`Current FY: ${gratuityData.current_financial_year}`, 146, 51);
    
    // Filter data based on current filters
    let filteredData = gratuityData.monthly_breakdown || [];
    if (filterFY !== "all") {
      filteredData = filteredData.filter(item => item.financial_year === filterFY);
    }
    if (searchTerm) {
      filteredData = filteredData.filter(item => 
        item.month.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.year.toString().includes(searchTerm)
      );
    }
    
    // Table headers
    const tableColumn = [
      "S.No",
      "Month",
      "Year",
      "Financial Year",
      "Basic Salary",
      "Monthly Gratuity",
      "Cumulative"
    ];
    
    // Create table rows
    const tableRows = filteredData.map((item, index) => [
      String(index + 1),
      item.month,
      String(item.year),
      item.financial_year,
      item.basic_salary,
      item.gratuity_amount,
      item.cumulative_till_month
    ]);
    
    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 60,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [76, 175, 80] },
      columnStyles: {
        0: { cellWidth: 10 },
        1: { cellWidth: 25 },
        2: { cellWidth: 15 },
        3: { cellWidth: 25 },
        4: { cellWidth: 30,},
        5: { cellWidth: 35,},
        6: { cellWidth: 35,}
      }
    });
    
    doc.save(`Gratuity_Statement_${employeeId}_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  // Export to Excel function
  const exportToExcel = () => {
    if (!gratuityData) return;
    
    // Monthly Breakdown Sheet
    const monthlyData = gratuityData.monthly_breakdown.map((item, index) => ({
      'S.No': index + 1,
      'Month': item.month,
      'Year': item.year,
      'Financial Year': item.financial_year,
      'Basic Salary': item.basic_salary,
      'Monthly Gratuity': item.gratuity_amount,
      'Cumulative': item.cumulative_till_month,
      'Gratuity Rate': '4.67%'
    }));
    
    // Yearly Summary Sheet
    const yearlyData = gratuityData.yearly_summary.map((year, index) => ({
      'S.No': index + 1,
      'Financial Year': year.financial_year,
      'Total Gratuity': year.total_gratuity,
      'Months Count': year.months_count,
      'Average Basic Salary': Math.round(year.average_basic),
      'Monthly Average Gratuity': Math.round(year.total_gratuity / year.months_count)
    }));
    
    // Summary Sheet
    const summaryData = [{
      'Employee Name': gratuityData.employee_name,
      'Employee ID': gratuityData.employee_id,
      'Total Accumulated Gratuity': gratuityData.total_gratuity_accumulated,
      'Current FY Gratuity': gratuityData.current_fy_gratuity,
      'Current Financial Year': gratuityData.current_financial_year,
      'Projected Annual Gratuity': gratuityData.projected_annual_gratuity,
      'Report Generated On': formatDate(new Date())
    }];
    
    // Create workbook and sheets
    const workbook = XLSX.utils.book_new();
    
    const monthlyWorksheet = XLSX.utils.json_to_sheet(monthlyData);
    const yearlyWorksheet = XLSX.utils.json_to_sheet(yearlyData);
    const summaryWorksheet = XLSX.utils.json_to_sheet(summaryData);
    
    // Set column widths
    const monthlyCols = [
      { wch: 5 },  // S.No
      { wch: 10 }, // Month
      { wch: 6 },  // Year
      { wch: 15 }, // Financial Year
      { wch: 15 }, // Basic Salary
      { wch: 15 }, // Monthly Gratuity
      { wch: 15 }, // Cumulative
      { wch: 10 }  // Gratuity Rate
    ];
    monthlyWorksheet['!cols'] = monthlyCols;
    
    const yearlyCols = [
      { wch: 5 },  // S.No
      { wch: 15 }, // Financial Year
      { wch: 15 }, // Total Gratuity
      { wch: 10 }, // Months Count
      { wch: 18 }, // Average Basic Salary
      { wch: 20 }  // Monthly Average Gratuity
    ];
    yearlyWorksheet['!cols'] = yearlyCols;
    
    XLSX.utils.book_append_sheet(workbook, monthlyWorksheet, 'Monthly Breakdown');
    XLSX.utils.book_append_sheet(workbook, yearlyWorksheet, 'Yearly Summary');
    XLSX.utils.book_append_sheet(workbook, summaryWorksheet, 'Summary');
    
    XLSX.writeFile(workbook, `Gratuity_Details_${employeeId}_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // Prepare chart data
  const prepareChartData = () => {
    if (!trendData || trendData.length === 0) return null;
    
    let filteredData = trendData;
    if (filterFY !== "all") {
      filteredData = trendData.filter(item => item.financial_year === filterFY);
    }
    
    if (searchTerm) {
      filteredData = filteredData.filter(item => 
        item.period.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    const labels = filteredData.map(item => item.period);
    const gratuityValues = filteredData.map(item => item.gratuity);
    const basicValues = filteredData.map(item => item.basic_salary);
    
    return {
      labels,
      datasets: [
        {
          label: 'Monthly Gratuity (₹)',
          data: gratuityValues,
          borderColor: '#4CAF50',
          backgroundColor: chartType === 'bar' ? '#4CAF50' : 'rgba(76, 175, 80, 0.1)',
          tension: 0.4,
          fill: chartType === 'line',
          yAxisID: 'y',
        },
        {
          label: 'Basic Salary (₹)',
          data: basicValues,
          borderColor: '#2196F3',
          backgroundColor: chartType === 'bar' ? '#2196F3' : 'rgba(33, 150, 243, 0.1)',
          tension: 0.4,
          fill: false,
          yAxisID: 'y',
        }
      ]
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Gratuity Growth Over Time',
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            return `${context.dataset.label}: ₹${context.parsed.y.toLocaleString()}`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value) => `₹${value.toLocaleString()}`
        }
      }
    }
  };

  const chartData = prepareChartData();

  // Get unique financial years for filter - use availableFY from API response
  const financialYears = availableFY.length > 0 ? availableFY : [];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!gratuityData) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">No gratuity data available</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', minHeight: '100vh', p: 3 }}>
      {/* Header */}
      <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
         
          <Grid item xs>
            <Typography variant="h4" fontWeight="bold" >
              Gratuity Overview
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {gratuityData.employee_name} • Employee ID: {gratuityData.employee_id} 
            </Typography>
          </Grid>
          <Grid item>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Export to Excel">
                <Button 
                  variant="outlined" 
                  startIcon={<DownloadIcon />}
                  onClick={exportToExcel}
                  sx={{ mr: 1 }}
                  color="success"
                >
                  Excel
                </Button>
              </Tooltip>
              <Tooltip title="Export to PDF">
                <Button 
                  variant="outlined" 
                  startIcon={<PrintIcon />}
                  onClick={exportToPDF}
                  sx={{ mr: 1 }}
                  color="primary"
                >
                  PDF
                </Button>
              </Tooltip>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Main Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card elevation={3} sx={{ borderRadius: 2, height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar sx={{ bgcolor: '#4CAF20', width: 40, height: 40, mr: 1 }}>
                  <AccountBalanceIcon />
                </Avatar>
                <Typography variant="h6" fontWeight="bold">
                  Total Accumulated
                </Typography>
              </Box>
              <Typography variant="h6" color="#4CAF50" fontWeight="bold" sx={{ mb: 1 }}>
                ₹{formatCurrency(gratuityData.total_gratuity_accumulated)}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Lifetime gratuity as of {formatDate(new Date())}
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={75} 
                sx={{ mt: 2, height: 8, borderRadius: 4 }}
              />
              <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                Based on 4.67% of basic salary per month
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card elevation={3} sx={{ borderRadius: 2, height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar sx={{ bgcolor: '#2196F3', width: 40, height: 40, mr: 1 }}>
                  <CalendarIcon />
                </Avatar>
                <Typography variant="h6" fontWeight="bold">
                  Current Financial Year
                </Typography>
              </Box>
              <Typography variant="h6" color="#2196F3" fontWeight="bold">
                ₹{formatCurrency(gratuityData.current_fy_gratuity)}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                FY {gratuityData.current_financial_year}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                <Chip 
                  label={`${Math.round((gratuityData.current_fy_gratuity / gratuityData.projected_annual_gratuity) * 100)}% of annual projection`} 
                  size="small" 
                  color="primary"
                  variant="outlined"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card elevation={3} sx={{ borderRadius: 2, height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar sx={{ bgcolor: '#FF9800', width: 40, height: 40, mr: 1 }}>
                  <TrendingUpIcon />
                </Avatar>
                <Typography variant="h6" fontWeight="bold">
                  Projected Annual
                </Typography>
              </Box>
              <Typography variant="h6" color="#FF9800" fontWeight="bold">
                ₹{formatCurrency(gratuityData.projected_annual_gratuity)}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Based on current basic salary
              </Typography>
              <Tooltip title="Projection assumes current basic salary remains unchanged">
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
                  *Estimated amount
                </Typography>
              </Tooltip>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
<Typography sx={{mb:2}}>
<Alert severity="info" >To be eligible for gratuity in Karnataka, an employee must complete a minimum of 5 years of continuous service with the same employer.</Alert>
</Typography>
      {/* Tabs for different views */}
      <Paper elevation={2} sx={{ borderRadius: 2, mb: 3 }}>
             <Tabs 
          value={tabValue} 
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab icon={<TableIcon />} label="Monthly Breakdown" />
          <Tab icon={<ShowChartIcon />} label="Yearly Summary" />
        </Tabs>


        {/* Tab 1: Monthly Breakdown Table */}
        {tabValue === 0 && (
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
              <TextField
                placeholder="Search by month/year..."
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }}
                sx={{ width: 300 }}
              />
              <FormControl size="small" sx={{ width: 200 }}>
                <InputLabel>Financial Year</InputLabel>
                <Select 
                  value={filterFY} 
                  onChange={(e) => setFilterFY(e.target.value)} 
                  label="Financial Year"
                >
                  <MenuItem value="all">All Years</MenuItem>
                  {financialYears.map((fy) => (
                    <MenuItem key={fy} value={fy}>{fy}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <TableContainer component={Paper} elevation={1}>
              <Table>
                <TableHead sx={{ bgcolor: '#f8f9fa' }}>
                  <TableRow>
                    <TableCell><strong>S.No</strong></TableCell>
                    <TableCell><strong>Month</strong></TableCell>
                    <TableCell><strong>Year</strong></TableCell>
                    <TableCell><strong>Financial Year</strong></TableCell>
                    <TableCell align="right"><strong>Basic Salary (₹)</strong></TableCell>
                    <TableCell align="right"><strong>Monthly Gratuity (₹)</strong></TableCell>
                    <TableCell align="right"><strong>Cumulative (₹)</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {gratuityData.monthly_breakdown
                    .filter(item => 
                      (filterFY === "all" || item.financial_year === filterFY) &&
                      (searchTerm === "" || 
                        item.month.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        item.year.toString().includes(searchTerm))
                    )
                    .map((item, index) => (
                      <TableRow key={index} hover>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>{item.month}</TableCell>
                        <TableCell>{item.year}</TableCell>
                        <TableCell>
                          <Chip label={item.financial_year} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell align="right">₹{formatCurrency(item.basic_salary)}</TableCell>
                        <TableCell align="right" sx={{ color: '#4CAF50', fontWeight: 'bold' }}>
                          ₹{formatCurrency(item.gratuity_amount)}
                        </TableCell>
                        <TableCell align="right">₹{formatCurrency(item.cumulative_till_month)}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination Component */}
            {gratuityData.pagination && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <TablePagination
                  component="div"
                  count={gratuityData.pagination.total_items}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  labelRowsPerPage="Rows per page:"
                  labelDisplayedRows={({ from, to, count }) => 
                    `${from}-${to} of ${count}`
                  }
                  sx={{
                    '.MuiTablePagination-selectLabel': {
                      fontWeight: 'normal'
                    },
                    '.MuiTablePagination-displayedRows': {
                      fontWeight: 'normal'
                    }
                  }}
                />
              </Box>
            )}
          </Box>
        )}

        {/* Tab 2: Yearly Summary */}
        {tabValue === 1 && (
          <Box sx={{ p: 3 }}>
            <Grid container spacing={3}>
              {gratuityData.yearly_summary.map((year, index) => (
                <Grid item xs={12} md={6} key={index}>
                  <Card elevation={2}>
                    <CardContent>
                      <Typography variant="h6" fontWeight="bold" color="#2c3e50" gutterBottom>
                        FY {year.financial_year}
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="textSecondary">
                            Total Gratuity
                          </Typography>
                          <Typography variant="h5" color="#4CAF50" fontWeight="bold">
                            ₹{formatCurrency(year.total_gratuity)}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="textSecondary">
                            Months
                          </Typography>
                          <Typography variant="h5" fontWeight="bold">
                            {year.months_count}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="body2" color="textSecondary">
                            Average Basic Salary
                          </Typography>
                          <Typography variant="h6">
                            ₹{formatCurrency(Math.round(year.average_basic))}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="body2" color="textSecondary">
                            Monthly Average Gratuity
                          </Typography>
                          <Typography variant="h6">
                            ₹{formatCurrency(Math.round(year.total_gratuity / year.months_count))}
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default GratuityTab;